import React, {useCallback, useRef} from 'react';
import {useDropzone} from 'react-dropzone';
import PropTypes from 'prop-types';
import css from '../FileUpload/FileUpload.module.scss';
import ImageAdd from "../../Icons/ImageAdd.jsx";
import Trash01 from "../../Icons/Trash01.jsx";
import ArrowSwitchVertical from "../../Icons/ArrowSwitchVertical.jsx";
import useDragAndDrop from "../../../hooks/useDragAndDrop.js";
import {colors} from '../../../utils/colors.js'

const ImageUpload = ({
  onImagesChange, 
  images = [],
  label,
  required = false,
  maxImages = 10,
  error,
  onImageDelete,
  imgClassName,
  enableDragAndDrop = false,
  onSortChange,
}) => {
  const fileInputRef = useRef(null);

  const hideDropzone = images.length >= maxImages;

  const {
    draggedIndex,
    dragOverIndex,
    onDragStart,
    onDragEnter,
    onDragLeave,
    onDragEnd,
    onDragOver
  } = useDragAndDrop(images, onImagesChange, onSortChange);

  const handleFiles = useCallback((files) => {
    const newImages = Array.from(files)
      .filter(file => file.type === 'image/png' || file.type === 'image/jpeg' || file.type === 'image/jpg')
      .map(file => {
        const preview = URL.createObjectURL(file);
        return {
          file: file,
          preview: preview
        };
      });

    const updatedImages = maxImages === 1 
      ? newImages.slice(0, 1)
      : [...images, ...newImages].slice(0, maxImages);

    onImagesChange?.(updatedImages);
  }, [onImagesChange, maxImages]);

  const onDrop = useCallback((acceptedFiles) => {
    handleFiles(acceptedFiles);
  }, [handleFiles]);

  const handleClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileInputChange = useCallback((e) => {
    handleFiles(e.target.files);
  }, [handleFiles]);

  const { getRootProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/png': ['.png'],
      'image/jpeg': ['.jpg', '.jpeg']
    },
    noClick: true
  });

  const deleteImage = async (index) => {
    try {
      const newImages = [...images];
      if (newImages[index] && newImages[index].id) {
        await onImageDelete(newImages[index].id)
      }
      URL.revokeObjectURL(newImages[index].preview);
      newImages.splice(index, 1);
      onImagesChange?.(newImages);
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <div className={css.fileUpload}>
      {label &&
        <label className={css.label}>
          {label}
          {required && <span className={css.required}>*</span>}
        </label>
      }
      <div className={css.uploaderContainer}>
        {images.map((image, index) => {
          const isFile = image.preview instanceof File || image instanceof Blob;
          const isDragging = draggedIndex === index;
          const isDragOver = dragOverIndex === index;
          const isDraggable = enableDragAndDrop && images.length > 1;
          
          return (
            <div
              key={image.id || image.preview}
              className={`${css.previewItem} ${imgClassName} ${isDragging ? css.dragging : ''} ${isDragOver ? css.dragOver : ''}`}
              draggable={isDraggable}
              onDragStart={() => isDraggable && onDragStart(index)}
              onDragEnter={(e) => isDraggable && onDragEnter(index)}
              onDragLeave={() => isDraggable && onDragLeave(index)}
              onDragEnd={() => isDraggable && onDragEnd()}
              onDragOver={(e) => isDraggable && onDragOver(e, index)}
              style={{
                cursor: isDraggable ? 'move' : 'default',
                opacity: isDragging ? 0.5 : 1,
              }}
            >
              <img src={isFile ? URL.createObjectURL(image.preview) : image.preview} alt={`Preview ${index + 1}`} />
              <button
                type="button"
                className={css.deleteButton}
                onClick={(e) => {
                  e.stopPropagation();
                  deleteImage(index);
                }}
              >
                <Trash01 color="var(--danger)" width={14}/>
              </button>
              {isDraggable &&
                <button
                  type="button"
                  className={css.dragHandle}
                  onMouseDown={(e) => {e.stopPropagation();}}
                >
                  <ArrowSwitchVertical color={colors.grey64} width={14}/>
                </button>
              }
            </div>
          )
        })}
        {!hideDropzone &&
          <div
            {...getRootProps()}
            className={`${css.dropzone} ${imgClassName} ${isDragActive ? css.active : ''} ${error ? css.error : ''}`}
            onClick={handleClick}
            data-error={!!error}
          >
            <input
              ref={fileInputRef}
              type="file"
              multiple={maxImages > 1}
              accept="image/png,image/jpeg"
              onChange={handleFileInputChange}
              style={{display: 'none'}}
              required={required && images.length === 0}
            />
            {isDragActive ?
              <p>Drop the images here...</p>
              :
              <div className={css.content}>
                <ImageAdd width={20} color="var(--primary-color)"/>
                <div className={css.uploadText}>
                  <span className={css.clickText}>Click to upload</span> or drag and drop
                  <div className={css.supportText}>PNG or JPG</div>
                </div>
              </div>
            }
          </div>
        }
      </div>
      {error && <span className={css.errorMessage}>{error}</span>}
    </div>
  );
};

ImageUpload.propTypes = {
  onImagesChange: PropTypes.func.isRequired,
  images: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.any,
      preview: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(File), PropTypes.instanceOf(Blob)]),
      file: PropTypes.instanceOf(File)
    })
  ),
  label: PropTypes.string,
  required: PropTypes.bool,
  maxImages: PropTypes.number,
  error: PropTypes.string,
  onImageDelete: PropTypes.func,
  imgClassName: PropTypes.string,
  enableDragAndDrop: PropTypes.bool,
  onSortChange: PropTypes.func
};

export default ImageUpload;
